// Ported from design_ideas/lernwelt/bundle.jsx — tilting scale for comparison questions

type Side = 'left' | 'right';

interface PanProps {
  value: number | string;
  side: Side;
  selected: boolean;
  isRight: boolean;
  isWrong: boolean;
  onClick: () => void;
  disabled: boolean;
}

function BalancePan({ value, side, selected, isRight, isWrong, onClick, disabled }: PanProps) {
  const bg = isRight ? 'var(--correct-soft)'
    : isWrong ? 'var(--wrong-soft)'
    : selected ? 'var(--amber-soft)'
    : 'var(--parchment)';
  const border = isRight ? 'var(--correct)'
    : isWrong ? 'var(--wrong)'
    : selected ? 'var(--amber)'
    : 'rgba(60,50,40,0.18)';

  return (
    <button
      role="radio"
      aria-checked={selected}
      aria-label={`Schild ${side === 'left' ? 'A' : 'B'}: ${value}`}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '26px 16px 20px',
        background: bg,
        border: `2px solid ${border}`,
        borderRadius: 3,
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'var(--display)',
        fontSize: 52,
        fontWeight: 600,
        color: 'var(--ink)',
        transition: 'all 0.15s',
        position: 'relative',
        minHeight: 120,
        width: '100%',
      }}
    >
      <div style={{
        position: 'absolute', top: 10, left: 12,
        fontFamily: 'var(--mono)',
        fontSize: 9, letterSpacing: '0.2em',
        fontWeight: 600,
        color: 'var(--ink-muted)',
        textTransform: 'uppercase',
      }}>Schild {side === 'left' ? 'A' : 'B'}</div>
      {value}
    </button>
  );
}

interface Props {
  left: number | string;
  right: number | string;
  /** Correct side, or null until the server has graded the answer. */
  correct: Side | null;
  selected: Side | null;
  setSelected: (side: Side) => void;
  submitted: boolean;
  disabled: boolean;
}

export default function Balance({ left, right, correct, selected, setSelected, submitted, disabled }: Props) {
  const tilt = !selected ? 0 : selected === 'left' ? -6 : 6;

  return (
    <div
      role="radiogroup"
      aria-label="Waage — welche Seite ist größer?"
      style={{
        marginBottom: 20,
        padding: '20px 20px 16px',
        background: 'var(--card)',
        border: '1px solid rgba(60,50,40,0.12)',
        borderRadius: 4,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* pivot triangle */}
      <div style={{
        position: 'absolute',
        left: '50%', bottom: 20,
        transform: 'translateX(-50%)',
        width: 0, height: 0,
        borderLeft: '16px solid transparent',
        borderRight: '16px solid transparent',
        borderBottom: '24px solid var(--ink)',
        zIndex: 0,
      }} />
      {/* beam */}
      <div style={{
        position: 'absolute',
        left: 20, right: 20, bottom: 38,
        height: 2, background: 'var(--ink)',
        transformOrigin: '50% 50%',
        transform: `rotate(${tilt}deg)`,
        transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
        zIndex: 1,
      }} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 20,
        alignItems: 'end',
        transform: `rotate(${tilt}deg)`,
        transformOrigin: '50% 100%',
        transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        <BalancePan
          value={left}
          side="left"
          selected={selected === 'left'}
          isRight={submitted && correct === 'left'}
          isWrong={submitted && correct !== null && selected === 'left' && correct !== 'left'}
          onClick={() => !submitted && !disabled && setSelected('left')}
          disabled={disabled || submitted}
        />
        <BalancePan
          value={right}
          side="right"
          selected={selected === 'right'}
          isRight={submitted && correct === 'right'}
          isWrong={submitted && correct !== null && selected === 'right' && correct !== 'right'}
          onClick={() => !submitted && !disabled && setSelected('right')}
          disabled={disabled || submitted}
        />
      </div>

      <div style={{
        display: 'flex', justifyContent: 'space-between',
        marginTop: 18, paddingTop: 10,
        borderTop: '1px dashed rgba(60,50,40,0.15)',
        fontFamily: 'var(--mono)',
        fontSize: 10, letterSpacing: '0.18em',
        color: 'var(--ink-muted)',
        textTransform: 'uppercase',
      }}>
        <span>← Linker Pfad</span>
        <span>Wähle den größeren</span>
        <span>Rechter Pfad →</span>
      </div>
    </div>
  );
}
